import { defaultTo, flatMap, get, isEmpty, isEqual, uniq } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { unstable, hooks, utils } from '@broadleaf/admin-components';

const { log } = utils;
const {
  ComponentRouterUtils,
  ContextRequest,
  useGatewayState,
  useCurrentApplication,
  I18nContext,
  useTracking,
  useActionCreators,
  UserAccessDispatchContext,
  ComponentRouterContext,
  UserAccess
} = unstable;

const { useRefreshEffect } = hooks;

const { ContextParameters } = ContextRequest;
const { getCachedRoutes, cacheRoutes, fetchRoutes } = ComponentRouterUtils;
const { getUserOperations } = UserAccess.actions;
const logger = log.getLogger('gateway.components.AuroraComponentRouter');

const AuroraComponentRouter = props => {
  /**
   * The current set of `ComponentRoute` for the application.
   */
  const [routes, setRoutes] = useState(defaultTo(getCachedRoutes(), []));
  const gateway = useGatewayState(props.ready);
  const application = useCurrentApplication();
  const applicationId = application && application.id;
  const locale = useContext(I18nContext).currentLocale;
  const tracking = useTracking();
  const context = useMemo(
    () => ({
      [ContextParameters.LOCALE]: locale,
      [ContextParameters.TRACKING]: tracking
    }),
    [locale, tracking]
  );

  const actions = useActionCreators(UserAccessDispatchContext, {
    /**
     * Callback used for retrieving the user permissions for all of the possible
     * routes once the routes are retrieved.
     *
     * @see #getUserOperations
     */
    getUserOperations
  });

  useEffect(
    /**
     * This is the primary effect used to ensure the routes are initialized for this gateway.
     * When ready, this will fetch the routes and provide them to children components
     */
    () => {
      (async () => {
        if (gateway.isReady()) {
          if (!isEmpty(routes)) {
            // if routes are already populated from the cache, we will render children immediately, but still hydrate the routes below
            gateway.setStatus(gateway.FINISHED);
          } else {
            // if routes are not populated from cache, we block rendering with a loading status
            gateway.setStatus(gateway.LOADING);
          }

          await loadRoutes({
            actions,
            context,
            setRoutes,
            routes: [],
            applicationId
          });

          gateway.setStatus(gateway.FINISHED);
        }
      })();
    },
    [actions, context, gateway, routes, applicationId]
  );

  useRefreshEffect(
    () => {
      (async () => {
        if (gateway.isFinished()) {
          // Modified to pass existing routes
          await loadRoutes({
            actions,
            context,
            setRoutes,
            routes,
            applicationId
          });
        }
      })();
    },
    { applicationId },
    undefined,
    false
  );
  const refetchRoutes = async () => {
    if (gateway.isFinished()) {
      await loadRoutes({
        actions,
        context,
        setRoutes,
        routes,
        applicationId
      });
    }
  };

  return (
    <ComponentRouterContext.Provider value={{ routes, refetchRoutes }}>
      {props.children(gateway)}
    </ComponentRouterContext.Provider>
  );
};

async function loadRoutes({
  actions,
  context,
  setRoutes,
  routes,
  applicationId
}) {
  try {
    // Hacky way to ensure we use the correct application ID when fetching user operations
    // --
    // Modified to pass all routes and an application ID as the last value
    // in the scopes array. This ensures we are always using the correct
    // application ID when fetching user operations.
    // If there is no application ID, the last scope is APPID_NONE
    // See userOperationsAxiosInterceptor.js
    const fetchedRoutes = await fetchRoutes(context);
    const allRoutes = [...routes, ...fetchedRoutes];
    const scopes = uniq(flatMap(allRoutes, 'scopes'));
    if (!isEmpty(scopes)) {
      const appIdScope = `APPID_${
        isEmpty(applicationId) ? 'NONE' : applicationId
      }`;
      scopes.push(appIdScope);
      actions.getUserOperations(scopes);
    }

    setRoutes(prevRoutes => {
      // if the routes are different, we set the new state
      if (!isEqual(prevRoutes, fetchedRoutes)) {
        cacheRoutes(fetchedRoutes);
        return fetchedRoutes;
      }

      return prevRoutes;
    });
  } catch (error) {
    // we purposely do not block rendering of children for an error here, as the user might still have functional Route components
    setRoutes([]);

    const status = get(error, 'response.status');
    const data = get(error, 'response.data');
    logger.error(
      '[ComponentRouter]: Failed retrieving route configuration. ' +
        'This most likely means either the routes endpoint was unable to be found, ' +
        'or a there was a problem with the service.\n\n' +
        `${status ? `Response Status: ${status}\n` : ''}` +
        `${
          data ? `Response Payload: ${JSON.stringify(data, null, 2)}\n` : ''
        }` +
        error.stack
    );
  }
}

export default AuroraComponentRouter;
